<template>
  <StepFormLayout
    class="candidate-editor-layout"
    ref="StepFormLayout"
    :class="[isNewPage && 'new-page-layout', hasResumeView && !onlyOpenQuestion && !onlyInviteQuestion ? 'has-resume-view' : 'none-resume-view']"
    :hiddenTitle="true"
    :activeMenuIndex="currentStepIndex"
    :stepMenuList="activeStepConfigList"
    :saveInLastStep="!isEditMode"
    :saveBtnTextKey="saveBtnTextKey"
    :hasResumeView="hasResumeView"
    :hiddenStepBtn="hiddenStepBtn"
    :showRightBtn="showRightBtn"
    :rightBtnTextI18nKey="rightBtnTextI18nKey"
    :showAgencyPolicy="showAgencyPolicy"
    :hiddenSaveBtn="stepSetting[STEP_TYPE.UPLOAD_RESUME]?.immediateUpload"
    :cancelBtnTextI18nKey="cancelBtnTextI18nKey"
    @step-change="handleStepChange"
    @shrink-change="handelShrinkChange"
    @prev="handlePrevBtnClick"
    @next="handleNextBtnClick"
    @save="handleSaveAndBackBtnClick"
    @cancel="handleCancelBtnClick"
    @rightBtnClick="handleRightBtnClick"
  >
    <component
      v-for="(item) in activeStepConfigList"
      v-bind="item.props"
      v-on="item.events"
      class="body-content"
      :key="item.stepType"
      :ref="`stepComponent_${item.stepType}`"
      :is="item.component"
      :show="showStep && item === stepConfig"
      :candidate-info="candidateInfo"
      :is-edit-mode="isEditMode"
      :isMiniResume="isMiniResume"
      :isBasicResume="isBasicResume"
      :hrMiniResumeSetting="hrMiniResumeSetting"
      :hasResumeView="hasResumeView"
      :jobId="jobId"
      :companyId="companyId"
      :onlyOpenQuestion="onlyOpenQuestion || onlyInviteQuestion"
      @hrSwitchMiniResumeChange="handleHrSwitchMiniResumeChange"
    />
    <template #resume-view v-if="hasResumeView && canShowResumeView">
      <div ref="viewer" v-show="showResumeView" class="resume-viewer-container">
        <PDFViewer v-if="viewResume.pdf" class="viewer" ref="relViewer" :fileURL="viewResume.fileURL" autoRender @endLoading="handelResumeViewLoading"/>
        <DocxViewer v-else-if="viewResume.docx" class="viewer" ref="relViewer" :fileURL="viewResume.fileURL" autoRender @endLoading="handelResumeViewLoading"/>
        <HTMLViewer v-else-if="viewResume.html" class="viewer" ref="relViewer" :fileURL="viewResume.fileURL" autoRender @endLoading="handelResumeViewLoading"/>
        <ImageViewer v-else-if="viewResume.image" class="viewer" ref="relViewer" :fileURL="viewResume.fileURL" autoRender @endLoading="handelResumeViewLoading"/>
        <RectLoading v-show="viewResume.loading" class="viewer-loading"></RectLoading>
      </div>
    </template>
  </StepFormLayout>
</template>

<script>
import _ from 'lodash'
import { mapActions } from 'vuex'
import StepFormLayout from '@/components/layout/stepLayout/formLayout.vue'
import UploadResume from './uploadResume.vue'
import BaseInfo from './baseInfo.vue'
import DuplicateCandidate from './duplicateCandidate.vue'
import Education from './educationInfo.vue'
import WorkExperience from './workExperienceInfo.vue'
import Language from './languageInfo.vue'
import AdditionalInfo from './additionalInfo.vue'
import Questionnaire, { QUESTIONNAIRE_TYPE } from './questionnaire.vue'
import SubmitReferral from './submitResume.vue'
import Description from './description.vue'
import { STEP_TYPE, CANDIDATE_OPEN_QUESTION_STEP_TYPE } from '@/constant/candidate'
import RectLoading from '@/components/loading/rectLoading.vue'

export default {
  components: {
    StepFormLayout,
    UploadResume,
    BaseInfo,
    Education,
    WorkExperience,
    Language,
    AdditionalInfo,
    Questionnaire,
    Description,
    PDFViewer: () => import('@/components/resumeViewer/pdf.vue'),
    DocxViewer: () => import('@/components/resumeViewer/docx.vue'),
    HTMLViewer: () => import('@/components/resumeViewer/html.vue'),
    ImageViewer: () => import('@/components/resumeViewer/image.vue'),
    RectLoading
  },
  props: {
    value: {
      type: Object,
      required: true
    },
    isCompanyJobEditable: {
      type: Boolean,
      default: true
    },
    isEditMode: {
      type: Boolean,
      default: false
    },
    isAddMode: {
      type: Boolean,
      default: false
    },
    hiddenIdNumber: {
      type: Boolean,
      default: true
    },
    stepSetting: {
      type: Object,
      required: false
    },
    isMiniResume: {
      type: Boolean,
      default: false
    },
    isBasicResume: {
      type: Boolean,
      default: false
    },
    isCandidate: {
      type: Boolean,
      default: false
    },
    isHr: {
      type: Boolean,
      default: false
    },
    isAgency: {
      type: Boolean,
      default: false
    },
    isNewPage: {
      type: Boolean,
      default: false
    },
    saveBtnTextKey: {
      type: String,
      default: ''
    },
    hrMiniResumeSetting: {
      type: Object
    },
    jobId: {
      type: String,
      default: null
    },
    companyId: {
      type: String,
      default: null
    },
    isFromPreference: {
      type: Boolean,
      default: false
    },
    cancelBtnTextI18nKey: {
      type: String,
      default: 'common.btn.cancel'
    }
  },
  data () {
    let mountPromiseResolve = null
    const mountPromise = new Promise((resolve, reject) => {
      mountPromiseResolve = resolve
    })
    const i18nPrefix = 'admin.application.candidate'
    const vm = this
    return {
      STEP_TYPE,
      mountPromise,
      mountPromiseResolve,
      i18nPrefix,
      candidateInfo: {},
      duplicateCandidateList: [],
      hrQuestionList: [],
      company: null,
      rootCompany: null,
      hasUnsavedStep: false,
      submittedStep: [],
      showStep: false,
      currentStepIndex: 0,
      stepConfigList: [
        {
          stepType: STEP_TYPE.UPLOAD_RESUME,
          labelI18nKey: `${i18nPrefix}.label.stepName.${STEP_TYPE.UPLOAD_RESUME}`,
          iconClass: 'iconfont iconupload',
          component: UploadResume,
          events: { change: this.handleInfoChange, uploadResume: this.handleUploadResume, create: this.handleNextBtnClick, save: this.handleSaveAndBackBtnClick },
          props: {
            setting: this.stepSetting?.[STEP_TYPE.UPLOAD_RESUME],
            isHrEditResume: this.isEditMode && this.isHr,
            isCandidate: this.isCandidate,
            isAgencyEditResume: this.isEditMode && this.isAgency,
            existResume: this.stepSetting?.profileStep > 1,
            skipParseResume: this.skipParseResume
          }
        },
        {
          stepType: STEP_TYPE.BASE_INFO,
          labelI18nKey: `${i18nPrefix}.label.stepName.${STEP_TYPE.BASE_INFO}`,
          iconClass: 'iconfont icona-BasicJobInformation',
          component: BaseInfo,
          props: {
            get isEmailEditable () { return vm.isEmailEditable },
            get isMobileEditable () { return vm.isMobileEditable },
            isHr: this.isHr,
            isAgency: this.isAgency,
            isCompanyJobEditable: this.isCompanyJobEditable,
            hiddenIdNumber: this.hiddenIdNumber,
            setting: this.stepSetting?.[STEP_TYPE.BASE_INFO]
          },
          events: {
            duplicateCandidateChange: this.onDuplicateCandidateChange,
            handleCompanyJobChange: this.onCompanyJobChange,
            handleImportResumeClick: this.onImportResumeClick,
            handleOnlyTalentPoolChange: this.onOnlyTalentPoolChange
          }
        },
        {
          stepType: STEP_TYPE.DUPLICATE_CANDIDATE,
          labelI18nKey: `${i18nPrefix}.label.stepName.${STEP_TYPE.DUPLICATE_CANDIDATE}`,
          iconClass: 'iconfont icona-duplicatedcandidate1',
          component: DuplicateCandidate,
          props: {
            get duplicateCandidateList () { return vm.duplicateCandidateList }
          }
        },
        {
          stepType: STEP_TYPE.EDUCATION,
          labelI18nKey: `${i18nPrefix}.label.stepName.${STEP_TYPE.EDUCATION}`,
          iconClass: 'iconfont iconeducation',
          component: Education,
          props: {
            setting: this.stepSetting?.[STEP_TYPE.EDUCATION],
            showEdit: this.stepSetting?.showEdit
          }
        },
        {
          stepType: STEP_TYPE.WORK_EXPERIENCE,
          labelI18nKey: `${i18nPrefix}.label.stepName.${STEP_TYPE.WORK_EXPERIENCE}`,
          iconClass: 'iconfont icona-workexperience',
          component: WorkExperience,
          props: {
            setting: this.stepSetting?.[STEP_TYPE.WORK_EXPERIENCE],
            showEdit: this.stepSetting?.showEdit
          }
        },
        {
          stepType: STEP_TYPE.LANGUAGE,
          labelI18nKey: `${i18nPrefix}.label.stepName.${STEP_TYPE.LANGUAGE}`,
          iconClass: 'iconfont iconlanguage',
          component: Language,
          props: {
            setting: this.stepSetting?.[STEP_TYPE.LANGUAGE],
            showEdit: this.stepSetting?.showEdit
          }
        },
        // {
        //   stepType: STEP_TYPE.ADDITIONAL_INFO,
        //   labelI18nKey: `${i18nPrefix}.label.stepName.${STEP_TYPE.ADDITIONAL_INFO}`,
        //   iconClass: 'iconfont icona-AdditionalInformation',
        //   component: AdditionalInfo,
        //   props: {
        //     setting: this.stepSetting?.[STEP_TYPE.ADDITIONAL_INFO]
        //   }
        // },
        {
          stepType: STEP_TYPE.DESCRIPTION,
          labelI18nKey: `${i18nPrefix}.label.stepName.${STEP_TYPE.DESCRIPTION}`,
          iconClass: 'iconfont icona-recruitercomments',
          component: Description,
          props: {
            setting: this.stepSetting?.[STEP_TYPE.DESCRIPTION]
          }
        },
        {
          stepType: STEP_TYPE.PRESCREEN_QUESTIONNAIRE,
          labelI18nKey: `${i18nPrefix}.label.stepName.${STEP_TYPE.PRESCREEN_QUESTIONNAIRE}`,
          iconClass: 'iconfont icona-ApplicantQuestionnaireScreening',
          component: Questionnaire,
          props: {
            infoPropName: 'open_question.prescreen',
            questionnaireType: QUESTIONNAIRE_TYPE.OPEN_QUESTION,
            setting: this.stepSetting?.[STEP_TYPE.PRESCREEN_QUESTIONNAIRE]
          }
        },
        {
          stepType: STEP_TYPE.JOB_QUESTIONNAIRE,
          labelI18nKey: `${i18nPrefix}.label.stepName.${STEP_TYPE.JOB_QUESTIONNAIRE}`,
          iconClass: 'iconfont icona-ApplicantQuestionnaireScreening',
          component: Questionnaire,
          props: {
            infoPropName: 'open_question.standard',
            showQrImagePropName: 'open_question.showStandardQrImage',
            questionnaireType: QUESTIONNAIRE_TYPE.OPEN_QUESTION,
            setting: this.stepSetting?.[STEP_TYPE.JOB_QUESTIONNAIRE]
          }
        },
        {
          stepType: STEP_TYPE.INVITE_QUESTIONNAIRE,
          labelI18nKey: `${i18nPrefix}.label.stepName.${STEP_TYPE.INVITE_QUESTIONNAIRE}`,
          iconClass: 'iconfont icona-ApplicantQuestionnaireScreening',
          component: Questionnaire,
          props: {
            infoPropName: 'open_question.invite',
            showQrImagePropName: 'open_question.showInviteQrImage',
            questionnaireType: QUESTIONNAIRE_TYPE.OPEN_QUESTION,
            setting: this.stepSetting?.[STEP_TYPE.INVITE_QUESTIONNAIRE]
          }
        },
        {
          stepType: STEP_TYPE.HR_QUESTIONNAIRE,
          labelI18nKey: `${i18nPrefix}.label.stepName.${STEP_TYPE.HR_QUESTIONNAIRE}`,
          iconClass: 'iconfont icona-ApplicantQuestionnaireScreening',
          component: Questionnaire,
          props: {
            infoPropName: 'open_question.hr',
            questionnaireType: QUESTIONNAIRE_TYPE.OPEN_QUESTION,
            setting: this.stepSetting?.[STEP_TYPE.HR_QUESTIONNAIRE],
            get hrQuestionList () { return vm.hrQuestionList }
          }
        },
        {
          stepType: STEP_TYPE.ONBOARDING_QUESTIONNAIRE,
          labelI18nKey: `${i18nPrefix}.label.stepName.${STEP_TYPE.ONBOARDING_QUESTIONNAIRE}`,
          iconClass: 'iconfont icona-ApplicantQuestionnaireScreening',
          component: Questionnaire,
          props: {
            infoPropName: 'onboarding',
            questionnaireType: QUESTIONNAIRE_TYPE.ONBOARDING,
            setting: this.stepSetting?.[STEP_TYPE.ONBOARDING_QUESTIONNAIRE]
          }
        },
        {
          stepType: STEP_TYPE.SUBMIT_REFERRAL,
          labelI18nKey: `${i18nPrefix}.label.stepName.${STEP_TYPE.SUBMIT_REFERRAL}`,
          iconClass: 'iconfont iconupload',
          component: SubmitReferral,
          props: {
            infoPropName: 'submit_referral',
            setting: this.stepSetting?.[STEP_TYPE.SUBMIT_REFERRAL]
          }
        }
      ],
      isEmailEditable: true,
      isMobileEditable: true,
      viewResume: {
        show: false,
        type: null,
        get pdf () { return this.type === 'pdf' },
        get docx () { return this.type === 'docx' },
        get html () { return this.type === 'html' },
        get image () { return this.type === 'image' },
        fileURL: null,
        loading: false
      },
      viewResize: null,
      // 第一次打开页面为uploadResume时 不加载原始简历
      hiddenResumeView: true,
      isSaving: false
    }
  },
  metaInfo () {
    if (this.hasResumeView) {
      return {
        style: [
          {
            innerHTML: `.el-popper .el-scrollbar .el-select-dropdown__wrap ul li {
              font-size: 12px;
            }
            .el-popper .el-select-dropdown__empty {
              font-size: 12px;
            }
            .el-popper .el-scrollbar .el-autocomplete-suggestion__wrap ul li {
              font-size: 12px;
            }
            .el-popper .el-picker-panel__body-wrapper .el-picker-panel__body .el-date-picker__header-label {
              font-size: 14px;
            }
            `
          }
        ]
      }
    }
  },
  computed: {
    activeStepConfigList () {
      const hiddenStep = [STEP_TYPE.SUBMIT_REFERRAL]
      const displayStep = []
      if (!this.isCandidate && !this.hasUploadPermission) {
        hiddenStep.push(STEP_TYPE.UPLOAD_RESUME)
      }
      if (!this.hasDuplicateCandidatePermission) {
        hiddenStep.push(STEP_TYPE.DUPLICATE_CANDIDATE)
      }
      if (this.stepSetting?.hiddenStep?.length > 0) {
        hiddenStep.push(...this.stepSetting.hiddenStep)
      }
      if (this.stepSetting?.displayStep?.length > 0) {
        displayStep.push(...this.stepSetting.displayStep)
      }
      return this.stepConfigList.filter(item => {
        if (!item.disabled && displayStep.includes(item.stepType)) {
          return true
        }
        if (item.disabled || hiddenStep.includes(item.stepType)) {
          return false
        } else if ([STEP_TYPE.PRESCREEN_QUESTIONNAIRE, STEP_TYPE.JOB_QUESTIONNAIRE, STEP_TYPE.INVITE_QUESTIONNAIRE, STEP_TYPE.ONBOARDING_QUESTIONNAIRE].includes(item.stepType)) {
          const questionInfoList = _.get(this.candidateInfo, item.props.infoPropName)
          return questionInfoList && questionInfoList.length > 0
        } else if (item.stepType === STEP_TYPE.DUPLICATE_CANDIDATE) {
          return this.duplicateCandidateList && this.duplicateCandidateList.length > 0
        } else if (item.stepType === STEP_TYPE.HR_QUESTIONNAIRE) {
          if (this.isEditMode) {
            const questionInfoList = _.get(this.candidateInfo, item.props.infoPropName)
            return questionInfoList && questionInfoList.length > 0
          } else {
            return this.hrQuestionList && this.hrQuestionList.length > 0
          }
        }
        return true
      })
    },
    hasUploadPermission () {
      return this.hasAnyPermission('PERM_JOB_CANDIDATE_UPLOAD')
    },
    hasDuplicateCandidatePermission () {
      return this.hasAnyPermission('PREMIUM_DUPLICATE_CANDIDATE')
    },
    activeStepList () {
      return this.activeStepConfigList.map(item => item.stepType)
    },
    stepType () {
      if (this.$route.query.step !== undefined) {
        return this.$route.query.step
      } else {
        return null
      }
    },
    stepConfig () {
      if (this.activeStepConfigList !== undefined) {
        return this.activeStepConfigList.find(item => item.stepType === this.stepType)
      } else {
        return []
      }
    },
    skipParseResume () {
      return this.stepSetting?.skipParseResume
    },
    onlyOpenQuestion () {
      return this.stepSetting?.onlyOpenQuestion
    },
    onlyInviteQuestion () {
      return this.stepSetting?.onlyInviteQuestion
    },
    hasResumeView () {
      return this.viewResume.show
    },
    showRightBtn () {
      return this.stepSetting?.showRightBtn
    },
    rightBtnTextI18nKey () {
      return this.stepSetting?.rightBtnTextI18nKey
    },
    showResumeView () {
      if (this.activeStepList[this.currentStepIndex] === STEP_TYPE.UPLOAD_RESUME) {
        return false
      }
      return true
    },
    hiddenStepBtn () {
      let hidden = false
      const openQuestion = this.candidateInfo.open_question
      const currentStep = this.activeStepList[this.currentStepIndex]
      if ((currentStep === STEP_TYPE.JOB_QUESTIONNAIRE && openQuestion.showStandardQrImage) ||
        (currentStep === STEP_TYPE.INVITE_QUESTIONNAIRE && openQuestion.showInviteQrImage)) {
        hidden = true
      }
      return hidden
    },
    canShowResumeView () {
      return !this.hiddenResumeView && !this.onlyOpenQuestion && !this.onlyInviteQuestion && !this.hiddenStepBtn
    },
    showAgencyPolicy () {
      return this.isAgency && !this.isEditMode
    }
  },
  created () {
    this.watchJob()
  },
  mounted () {
    window.onbeforeunload = this.handleWindowBeforeUnload
    this.mountPromiseResolve()
    const stepEl = this.$refs.StepFormLayout.$el
    this.viewResize = new MutationObserver((mutationsList, observer) => {
      this.resetResumeViewHeight()
    })
    this.viewResize.observe(stepEl, { attributes: false, childList: true, subtree: true })
  },
  methods: {
    ...mapActions('application', ['getHrQuestion']),
    handleHrSwitchMiniResumeChange (value) {
      this.$emit('hrSwitchMiniResumeChange', value)
    },
    onDuplicateCandidateChange (duplicateCandidateList, resolve) {
      this.duplicateCandidateList = duplicateCandidateList
      this.$nextTick(() => resolve())
    },
    onCompanyJobChange (companyJob) {
      this.loadHrQuestion(companyJob)
    },
    onOnlyTalentPoolChange (onlyTalentPool) {
      this.$emit('onlyTalentPoolChange', onlyTalentPool)
    },
    onImportResumeClick () {
      this.$emit('importResume')
      this.changeCurrentStep(0)
    },
    watchJob () {
      const jobId = this.$route.params.jobId
      if (jobId !== '-1' && !this.isEditMode) {
        this.loadHrQuestion(jobId)
      }
      if (this.isCandidate) {
        this.$set(this.candidateInfo, 'open_question', {
          hr: this.hrQuestionList,
          standard: this.value.open_question?.standard,
          invite: this.value.open_question?.invite,
          showStandardQrImage: this.value.open_question?.showStandardQrImage,
          showInviteQrImage: this.value.open_question?.showInviteQrImage
        })
      }
    },
    async loadHrQuestion (companyJob) {
      const jobId = this.$route.params.jobId !== '-1' ? this.$route.params.jobId : companyJob
      if (!jobId) {
        this.$set(this.candidateInfo, 'open_question', {
          hr: []
        })
        this.hrQuestionList = []
        return
      }
      const resp = await this.getHrQuestion(jobId)
      if (resp) {
        this.$set(this.candidateInfo, 'open_question', {
          hr: resp
        })
        this.hrQuestionList = resp
      }
    },
    async submitCurrentStep (ignoreError) {
      if (this.isCurrentStepHasChangedData()) {
        this.hasUnsavedStep = true
      }
      const stepConfig = this.stepConfig
      const stepComponent = this.$refs[`stepComponent_${stepConfig.stepType}`][0]
      const success = await stepComponent.submit(ignoreError)
      const submittedStep = this.submittedStep
      if (success && !submittedStep.includes(stepConfig)) {
        submittedStep.push(stepConfig)
      } else if (!success) {
        _.remove(submittedStep, stepConfig)
      }
      return success
    },
    isCurrentStepHasChangedData () {
      const stepConfig = this.stepConfig
      if (stepConfig === null) {
        return false
      }
      const stepComponents = this.$refs[`stepComponent_${stepConfig.stepType}`]
      if (!stepComponents) {
        return false
      }
      return stepComponents[0].hasChangedData
    },
    async showStepPage (stepConfig) {
      if (!stepConfig || !this.activeStepList.includes(stepConfig.stepType)) {
        stepConfig = this.activeStepConfigList[0]
      }
      const stepType = stepConfig.stepType
      if (stepType === this.stepType) {
        if (this.isCurrentStepHasChangedData()) {
          const confirm = window.confirm(this.$t(`${this.i18nPrefix}.message.refreshPageWithoutSaveData`))
          if (!confirm) {
            return
          }
        }
        this.showStep = false
        await this.$nextTick()
        this.showStep = true
      } else {
        const query = { ...this.$route.query, step: stepType }
        if (this.stepType) {
          await this.$router.push({ query })
        } else {
          await this.$router.replace({ query })
        }
      }
      this.currentStepIndex = _.findIndex(this.activeStepConfigList, { stepType })
      await this.$nextTick()
      this.resetResumeViewHeight()
    },
    async handleStepChange (targetIndex) {
      this.changeCurrentStep(targetIndex)
    },
    async changeCurrentStep (targetIndex, indexDelta) {
      const originLen = this.activeStepConfigList.length
      const success = await this.submitCurrentStep() // 这个会导致 activeStepConfigList 产生变化：例如 有重复候选人时，会多一个 step
      const nowLen = this.activeStepConfigList.length
      if (targetIndex) {
        // 修正 submitCurrentStep 导致的 step 数量变化
        targetIndex += nowLen - originLen
      } else {
        targetIndex = this.activeStepConfigList.indexOf(this.stepConfig) + indexDelta
      }
      let targetStep = this.activeStepConfigList[targetIndex]
      const currentIndex = this.currentStepIndex
      let needCheck = false
      if (targetIndex > currentIndex) {
        if (!success) {
          return
        }
        const nextIndex = currentIndex + 1
        if (targetIndex > nextIndex) {
          const pastIndexList = _.range(nextIndex, targetIndex)
          _.forEach(pastIndexList, index => {
            const stepConfig = this.activeStepConfigList[index]
            if (!this.submittedStep.includes(stepConfig)) {
              targetStep = stepConfig
              needCheck = true
              return false
            }
          })
        }
        this.checkCandidateSaveResume(targetStep)
      }
      if (this.hiddenResumeView) {
        this.hiddenResumeView = false
      }
      await this.showStepPage(targetStep)
      if (needCheck) {
        // wait step change
        await this.$nextTick()
        // wait step data render
        await this.$nextTick()
        this.submitCurrentStep()
      }
    },
    hasUnsavedData () {
      return this.hasUnsavedStep || this.isCurrentStepHasChangedData()
    },
    handleWindowBeforeUnload ($event) {
      if (this.hasUnsavedData()) {
        return ''
      } else {
        window.onbeforeunload = null
      }
    },
    handlePrevBtnClick () {
      this.changeCurrentStep(null, -1)
    },
    handleCancelBtnClick () {
      if (this.hasUnsavedData()) {
        const confirm = window.confirm(this.$t(`${this.i18nPrefix}.message.refreshPageWithoutSaveData`))
        if (!confirm) {
          return
        }
      }
      window.onbeforeunload = null
      this.$emit('cancel')
    },
    handleRightBtnClick () {
      this.$emit('rightBtnClick')
    },
    async handleSaveAndBackBtnClick () {
      if (this.isSaving) {
        return
      }
      this.isSaving = true
      try {
        const success = await this.submitCurrentStep()
        if (!success) {
          return
        }
        if (this.isCandidate && CANDIDATE_OPEN_QUESTION_STEP_TYPE.includes(this.activeStepList[this.currentStepIndex])) {
          this.$emit('saveOpenQuestion', { ...this.candidateInfo })
        } else {
          this.$emit('save-back', { ...this.candidateInfo })
        }
        this.hasUnsavedStep = false
      } finally {
        this.isSaving = false
      }
    },
    checkCandidateSaveResume (targetStep) {
      // 求职值编辑简历时，完成职位编辑进入open question前保存简历同时完成第一步申请流程-编辑简历
      const currentStep = this.activeStepList[this.currentStepIndex]
      if (this.isCandidate && CANDIDATE_OPEN_QUESTION_STEP_TYPE.includes(targetStep.stepType) && !CANDIDATE_OPEN_QUESTION_STEP_TYPE.includes(currentStep)) {
        this.$emit('save', { ...this.candidateInfo }, true)
      }
    },
    handleNextBtnClick () {
      if (this.isFromPreference && this.stepSetting?.[STEP_TYPE.UPLOAD_RESUME].onlyUploadResume) {
        this.$emit('changeResumeSetting')
      }
      this.changeCurrentStep(null, 1)
    },
    handleInfoChange (info) {
      this.stepSetting.showEdit = false
      this.resetCandidateInfo(info, true)
      this.handleNextBtnClick()
      this.initResumeViewSetting(info.baseInfo)
    },
    handleUploadResume (resumeData) {
      this.resetCandidateInfo(resumeData)
      this.$emit('uploadResume', resumeData)
    },
    resetCandidateInfo (info, change) {
      if (info.isUpload && this.skipParseResume) {
        this.candidateInfo.resumePath = info.resumePath
        this.candidateInfo.isUpload = info.isUpload
        return
      }
      const baseInfo = info.baseInfo
      if (baseInfo) {
        baseInfo.currentCountry = baseInfo.currentCountryId
        baseInfo.currentProvince = baseInfo.currentProvinceId
        baseInfo.currentCity = baseInfo.currentCityId
        baseInfo.country = baseInfo.countryId
        baseInfo.province = baseInfo.provinceId
        baseInfo.city = baseInfo.cityId
      } else {
        info.baseInfo = {}
      }
      // 求职者编辑简历的时候，邮箱和手机号码是不可以通过常规的方式修改，只能通过特定的方式修改
      if (this.isCandidate && change) {
        baseInfo.mobile = this.candidateInfo.baseInfo?.mobile
        baseInfo.mobileCountryCode = this.candidateInfo.baseInfo?.mobileCountryCode
        baseInfo.emailAddress = this.candidateInfo.baseInfo?.emailAddress
      }

      const workExperienceInfo = info.workExperienceInfo
      if (workExperienceInfo) {
        _.forEach(workExperienceInfo.partTimeExperience, item => {
          item.industry = item.industryId
          item.salary = item.salaryId
        })
        _.forEach(workExperienceInfo.workExperience, item => {
          item.industry = item.industryId
          item.salary = item.salaryId
        })
      }

      if (!this.isCompanyJobEditable && this.candidateInfo?.baseInfo) {
        info.baseInfo.companyJob = this.candidateInfo.baseInfo.companyJob
      }
      const isUpload = info.isUpload
      this.isEmailEditable = !this.isEditMode || !baseInfo?.emailAddress
      this.isMobileEditable = !this.isEditMode || !baseInfo?.mobile
      if ((this.isHr || this.isAgency) && isUpload) {
        this.isEmailEditable = !this.isEditMode || !this.candidateInfo?.baseInfo?.emailAddress
        this.isMobileEditable = !this.isEditMode || !this.candidateInfo?.baseInfo?.mobile
      }
      if (this.isCandidate) {
        this.isEmailEditable = isUpload ? !this.candidateInfo?.baseInfo?.emailAddress : !baseInfo?.emailAddress
        this.isMobileEditable = false
        info.open_question = this.candidateInfo.open_question
      }
      // 2023.11.14 issue:6427 HR在编辑求职者的时候可以上传简历并替换原始简历,需要将快照，评论，标签，onboarding，open question等信息保存下来
      // 2025.02.25 issue:7630 Agency 编辑求职者时可以上传简历并替换原始简历
      if ((this.isHr || this.isAgency) && this.candidateInfo?.baseInfo) {
        info.avatarId = this.candidateInfo.avatarId
        info.commentAndTagInfo = this.candidateInfo.commentAndTagInfo
        info.onboarding = this.candidateInfo.onboarding
        info.open_question = this.candidateInfo.open_question
      }
      this.candidateInfo = info
    },
    handelResumeViewLoading () {
      this.viewResume.loading = false
      this.resetResumeViewHeight()
      window.addEventListener('resize', this.viewResume.resize)
    },
    async initResumeViewSetting (baseInfo) {
      this.viewResume.type = null
      this.viewResume.show = false
      this.viewResume.fileURL = null
      if (!baseInfo) {
        return
      }
      await this.$nextTick()
      if (this.viewResume.resize) {
        window.removeEventListener('resize', this.viewResume.resize)
      }
      if (['pdf', 'image', 'html', 'docx'].includes(baseInfo.resume_file_type)) {
        this.viewResume.type = baseInfo.resume_file_type
        this.viewResume.show = true
        this.viewResume.loading = true
        this.viewResume.resize = _.debounce(() => {
          if (this.viewResume.pdf) {
            this.$refs.relViewer.forceRefresh()
          }
        }, 100)
        if (baseInfo.resume_file_url.startsWith('http')) {
          this.viewResume.fileURL = baseInfo.resume_file_url
        } else {
          if (process.env.NODE_ENV === 'production') {
            this.viewResume.fileURL = this.$DjangoMediaUrl(baseInfo.resume_file_url)
          } else {
            this.viewResume.fileURL = '/api/media/' + baseInfo.resume_file_url
          }
        }
      }
    },
    async resetResumeViewHeight () {
      if (!this.hasResumeView || !this.showResumeView) {
        return
      }
      await this.$nextTick()
      const el = this.$refs.viewer
      if (el) {
        const height = el.parentElement.lastElementChild.clientHeight
        const iframeHeight = el.firstElementChild.clientHeight
        if (height > iframeHeight + 3) {
          el.style.maxHeight = `${iframeHeight + 3}px`
        } else {
          el.style.maxHeight = `${height}px`
        }
      }
    },
    handelShrinkChange () {
      if (!this.hasResumeView || !this.showResumeView) {
        return
      }
      if (this.viewResume.pdf) {
        setTimeout(() => {
          this.$refs.relViewer.forceRefresh()
        }, 1000)
      }
    }
  },
  watch: {
    value: {
      immediate: true,
      async handler () {
        let candidateInfo = {}
        if (this.value) {
          candidateInfo = { ...this.value }
        }
        this.resetCandidateInfo(candidateInfo)
        this.$eventBus.$emit('showLoading', true)
        try {
          await this.mountPromise
          let stepConfig = (this.isEditMode || [STEP_TYPE.UPLOAD_RESUME, STEP_TYPE.BASE_INFO].includes(this.stepType)) ? this.stepConfig : this.activeStepConfigList[0]
          if (this.isEditMode) {
            this.submittedStep = _.map(this.stepConfigList, item => item)
          }
          const needUploadResume = this.stepSetting?.[STEP_TYPE.UPLOAD_RESUME]?.needUploadResume
          const profileStep = this.stepSetting?.profileStep
          if (profileStep === 2 && !needUploadResume) {
            stepConfig = this.activeStepConfigList[1]
          }
          if (this.activeStepConfigList[0].stepType !== STEP_TYPE.UPLOAD_RESUME) {
            stepConfig = this.activeStepConfigList[0]
          }
          if (!stepConfig || !this.activeStepList.includes(stepConfig.stepType)) {
            stepConfig = this.activeStepConfigList[0]
          }
          if (stepConfig.stepType !== STEP_TYPE.UPLOAD_RESUME) {
            this.hiddenResumeView = false
          }
          await this.showStepPage(stepConfig)
          this.showStep = true
          this.initResumeViewSetting(this.candidateInfo.baseInfo)
        } finally {
          this.$eventBus.$emit('showLoading', false)
        }
      }
    }
  },
  destroyed () {
    if (this.viewResume.resize) {
      window.removeEventListener('resize', this.viewResume.resize)
    }
    if (this.viewResize) {
      this.viewResize.disconnect()
    }
  }
}
</script>

<style lang="scss" scoped>
.candidate-editor-layout {
  display: flex;
  margin: 0 auto;
  border-radius: 10px;
  background-color: #f7f7f7;
  box-sizing: border-box;
  position: relative;

  ::v-deep .layout-aside {
    .step-icon {
      .icona-duplicatedcandidate1 {
        font-size: 35px;

        &::before {
          margin-left: -10px;
        }
      }
    }
  }

  &.new-page-layout {
    ::v-deep {
      .layout-aside {
        border-radius: unset;
        padding: 27px 12px 50px 6px;
        background-color: #FFFFFF;

        .step-menu-item {

          .step-tail {
            z-index: 1;
          }

          .step-node {
            color: #606266;
            z-index: 1;

            &.available {
              color: #14a965;
            }
          }

          .step-icon {
            color: #606266;
          }
          .step-name {
            color: #606266;
          }
        }

        .step-menu-item.is-active {
          background-color: #F2FCFF;
          color: #0096cc;

          .step-node {
            color: #0096cc;
          }

          .step-icon {
            color: #0096cc;
          }
          .step-name {
            color: #0096cc;
          }

          .processing {
            background-color: #0096cc;
          }
        }
      }

      .layout-main {
        max-width: unset;
        margin-left: 20px;
        align-items: center;
        background-color: #FFFFFF;

        .layout-content {
          width: 980px;
        }
      }
    }
  }

  &.has-resume-view {
    ::v-deep {
      .layout-main {
        .layout-content {
          min-width: 980px;
          width: 100%;

          .form-container {
            justify-content: center;
            margin: 0px 0px 30px;

            .el-input {
              .el-input__suffix-inner {
                line-height: inherit;
              }
            }
          }

          .content {
            .editor {
              margin: 25px 35px 0 0;
            }
          }
        }
      }
    }
  }

  &.none-resume-view {
    ::v-deep {
      .upload-candidate {
        margin: 0 auto;
      }
    }
  }

  .layout-body {
    flex: 1;
    display: flex;
    width: 63vw;
    min-width: 980px;
    max-width: 1035px;
    padding: 60px 40px 50px;
    flex-direction: column;

    .body-title {
      padding-bottom: 15px;
      border-bottom: 1px solid #cccccc;
      font-size: 18px;
    }

    .body-content {
      flex: 1;
      margin: 50px;
    }

    .body-footer {
      &.footer-btn-group {
        display: flex;
        justify-content: space-between;

        .footer-btn {
          padding: 5px 25px;
          line-height: normal;

          &.cancel-btn {
            margin-right: auto;
          }
        }
      }
    }
  }

  .resume-viewer-container {
    border: 1px solid #C0C4CC;
    position: relative;
    overflow: auto;
    height: 100%;

    .viewer {
      display: block;
      width: 100%;
      max-width: 800px;
      margin: auto;
    }

    .viewer-loading {
      position: absolute;
    }
  }
}
</style>
